import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';
import { SkeletonTable } from './components/Skeleton';

interface BalanceSheetProps {
  entrepriseId: string;
  exerciceId: string;
}

interface BalanceEntry {
  compte_id: string;
  libelle: string;
  classe: number;
  debit_centimes: number;
  credit_centimes: number;
}

const formatCurrency = (centimes: number) => {
  const euros = centimes / 100;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(euros);
};

const BalanceSheet: React.FC<BalanceSheetProps> = ({ entrepriseId, exerciceId }) => {
  const { data: report, isLoading, error } = useQuery({
    queryKey: ['balance', entrepriseId, exerciceId],
    queryFn: () => api.balance.get(entrepriseId, exerciceId),
  });

  const exportCSV = () => {
    if (!report) return;
    const headers = ['Compte', 'Libellé', 'Classe', 'Débit (€)', 'Crédit (€)'];
    const rows = report.entries.map((e: BalanceEntry) => [e.compte_id, e.libelle, e.classe.toString(), (e.debit_centimes / 100).toFixed(2), (e.credit_centimes / 100).toFixed(2)]);
    const totals = ['TOTAUX', '', '', (report.total_debit_centimes / 100).toFixed(2), (report.total_credit_centimes / 100).toFixed(2)];
    const csv = [headers, ...rows, totals].map((r: string[]) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `balance-${entrepriseId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportFEC = async () => {
    try {
      const blob = await api.fec.get(exerciceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `fec-${exerciceId}.fec`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) { alert('Erreur FEC: ' + e.message); }
  };

  if (isLoading) return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Balance Générale</h2>
      <SkeletonTable rows={10} />
    </div>
  );
  if (error) return <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">Erreur: {(error as Error).message}</div>;
  if (!report) return null;

  const grouped = new Map<number, BalanceEntry[]>();
  for (const entry of report.entries) {
    const list = grouped.get(entry.classe) || [];
    list.push(entry);
    grouped.set(entry.classe, list);
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Balance Générale</h2>
          <p className="text-sm text-gray-500 mt-1">Entreprise: {report.entreprise_id} | Exercice: {report.exercice_id}</p>
        </div>
        <div className={`mt-2 md:mt-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${report.is_balanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {report.is_balanced ? '✔ Équilibrée' : '✖ Déséquilibre'}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <span className="text-sm text-gray-500">Total Débit</span>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(report.total_debit_centimes)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <span className="text-sm text-gray-500">Total Crédit</span>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(report.total_credit_centimes)}</p>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <button onClick={exportCSV} className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors">Exporter CSV</button>
        <button onClick={exportFEC} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">Exporter FEC</button>
      </div>

      {/* Single unified table with class header rows for aligned columns */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[120px]" />
            <col />
            <col className="w-[140px]" />
            <col className="w-[140px]" />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compte</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Libellé</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Débit</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Crédit</th>
            </tr>
          </thead>
          <tbody>
            {Array.from(grouped.keys()).sort((a, b) => a - b).map(classe => (
              <>
                <tr key={`class-${classe}`} className="bg-amber-50 border-y border-amber-100">
                  <td colSpan={4} className="px-4 py-2 text-xs font-bold text-amber-800 uppercase tracking-wide">
                    Classe {classe}
                  </td>
                </tr>
                {grouped.get(classe)!.map((entry: BalanceEntry) => (
                  <tr key={entry.compte_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{entry.compte_id}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 truncate">{entry.libelle}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right whitespace-nowrap">{formatCurrency(entry.debit_centimes)}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right whitespace-nowrap">{formatCurrency(entry.credit_centimes)}</td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BalanceSheet;
